import React from 'react';
import { Modal, Button } from "react-bootstrap";
import { IoWarningOutline } from "react-icons/io5";
import { IconBaseProps } from "react-icons";
import styles from './SessionDisconnectedModal.module.css';

const WarningIcon = IoWarningOutline as React.ComponentType<IconBaseProps>;

interface SessionDisconnectedModalProps {
  show: boolean;
  onRelogin: () => void;
}

const MESSAGE_ELEMENT_ID = "session-disconnected-message";

/**
 * Shown when the session can no longer be renewed — the refresh token has expired or is invalid,
 * so the socket has been disconnected and the session cannot be recovered silently.
 *
 * Deliberately has no dismiss path (no close button, no backdrop click): the only way forward is
 * to log in again, and clicking through would leave the user on a dead authenticated screen.
 */
const SessionDisconnectedModal: React.FC<SessionDisconnectedModalProps> = ({ show, onRelogin }) => {
  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      // The message is the dialog's accessible name: there is no title bar, and without this the
      // only interaction available to a screen-reader user is an unnamed dialog. Labelled by the
      // rendered text rather than a duplicated aria-label string, so the two cannot drift apart.
      aria-labelledby={MESSAGE_ELEMENT_ID}
      dialogClassName={styles.sessionDisconnectedDialog}
      contentClassName={styles.sessionDisconnectedContent}
      backdropClassName={styles.sessionDisconnectedBackdrop}
    >
      <Modal.Body className={styles.sessionDisconnectedBody}>
        <div className={styles.sessionDisconnectedIconContainer}>
          <WarningIcon size={48} className={styles.sessionDisconnectedWarningIcon} />
        </div>
        <p id={MESSAGE_ELEMENT_ID} className={styles.sessionDisconnectedMessage}>
          Your session is disconnected. Please re-login.
        </p>
      </Modal.Body>
      <Modal.Footer className={styles.sessionDisconnectedFooter}>
        <Button className={styles.sessionDisconnectedLoginBtn} onClick={onRelogin}>
          Re-login
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SessionDisconnectedModal;
